/**
 * `usePublicSettings` — the facts about this installation that a
 * signed-out screen needs, asked of the server.
 *
 * Today that is the terms and privacy pages signup asks people to agree
 * to. Each is set per installation, so a self-hosted copy that sets
 * neither shows no agreement box.
 *
 * **It never guesses.** Until the server answers, and when it fails, the
 * state says so. Signup must not show "no agreement needed" because a
 * request failed, and must not show pages the server did not name.
 *
 * Built like `useRunLimits`: one request shared by everyone who mounts at
 * once, cached for the session, and a failed request dropped from the
 * cache so a retry asks again.
 */
import { useCallback, useEffect, useState } from 'react'
import { publicSettingsAPI, type PublicSettings } from '@/lib/api'

/** The in-flight or settled request, shared by every caller. */
let inFlight: Promise<PublicSettings> | null = null

function fetchPublicSettings(): Promise<PublicSettings> {
  if (!inFlight) {
    inFlight = publicSettingsAPI.get().catch(err => {
      // Drop the failure so a retry asks again rather than reusing it.
      inFlight = null
      throw err
    })
  }
  return inFlight
}

/** Forget the cached settings, for tests. */
export function invalidatePublicSettings(): void {
  inFlight = null
}

export type PublicSettingsState =
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  | { status: 'ready'; settings: PublicSettings }

export function usePublicSettings(): PublicSettingsState {
  const [state, setState] = useState<PublicSettingsState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    setState({ status: 'loading' })
    setAttempt(n => n + 1)
  }, [])

  useEffect(() => {
    let cancelled = false
    fetchPublicSettings()
      .then(settings => { if (!cancelled) setState({ status: 'ready', settings }) })
      .catch(() => { if (!cancelled) setState({ status: 'error', retry }) })
    return () => { cancelled = true }
  }, [attempt, retry])

  return state
}

/**
 * Whether this installation can send email, for screens that offer it.
 *
 * `'loading'` until the server answers, so an email-off install does not
 * flash email buttons first. A failed request reads as `'on'`: the screen
 * behaves as it did before this check existed, and the server still
 * refuses or drops email it cannot send, so the screen never decides
 * what actually happens.
 */
export function useEmailStatus(): 'loading' | 'on' | 'off' {
  const state = usePublicSettings()
  if (state.status === 'loading') return 'loading'
  if (state.status === 'error') return 'on'
  return state.settings.email.enabled ? 'on' : 'off'
}
